import clsx from 'clsx';
import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { ClientAutoplayVideo } from './ClientAutoplayVideo.mjs';

const Video = forwardRef((props, ref) => {
  const {
    src,
    sources,
    poster,
    controls = true,
    autoPlay = false,
    loop = false,
    muted = false,
    playsInline = true,
    className,
    width = "100%",
    height = "100%",
    objectFit = "cover",
    objectPosition = "center",
    position = "relative",
    autoplayInView = false,
    forceAttributes = true,
    captions,
    decorative = false,
    describedByText,
    left,
    right,
    top,
    bottom,
    zIndex,
    ...rest
  } = props;
  if (autoplayInView) {
    return /* @__PURE__ */ React.createElement(
      ClientAutoplayVideo,
      {
        sources,
        src,
        loop,
        muted,
        controls,
        describedByText,
        style: { position, width, height, objectFit, objectPosition, left, right, top, bottom, zIndex },
        ...rest
      }
    );
  }
  const classNames = clsx("kohler-video-root", className);
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current);
  useEffect(() => {
    if (!forceAttributes) return;
    const el = innerRef.current;
    if (!el) return;
    const toggleAttr = (name, on) => {
      if (on) el.setAttribute(name, "");
      else el.removeAttribute(name);
    };
    toggleAttr("muted", muted);
    toggleAttr("controls", controls);
    toggleAttr("autoplay", autoPlay);
    toggleAttr("loop", loop);
    toggleAttr("playsinline", playsInline);
  }, [forceAttributes, muted, controls, autoPlay, loop, playsInline]);
  const descId = describedByText ? "video-desc-" + Math.random().toString(36).slice(2) : void 0;
  const hasSources = Array.isArray(sources) && sources.length > 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, describedByText && /* @__PURE__ */ React.createElement("span", { id: descId, className: "sr-only" }, describedByText), /* @__PURE__ */ React.createElement(
    "video",
    {
      ref: innerRef,
      className: classNames,
      src: hasSources ? void 0 : src,
      poster,
      controls: decorative ? false : controls,
      autoPlay,
      loop,
      muted,
      playsInline,
      "aria-hidden": decorative || void 0,
      tabIndex: decorative ? -1 : void 0,
      "aria-describedby": describedByText ? descId : void 0,
      style: {
        position,
        width,
        height,
        objectFit,
        objectPosition,
        left,
        right,
        top,
        bottom,
        zIndex
      },
      ...rest
    },
    hasSources && sources?.map((source) => /* @__PURE__ */ React.createElement("source", { key: source.src, ...source })),
    captions && /* @__PURE__ */ React.createElement(
      "track",
      {
        kind: "captions",
        src: captions.src,
        srcLang: captions.srclang,
        label: captions.label,
        ...captions.default ? { default: true } : {}
      }
    )
  ));
});

export { Video };
